import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, FileDown } from "lucide-react";
import { SignaturePad } from "../components/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD = "w-full bg-white border border-[#E3DECE] px-4 py-3 text-sm focus:outline-none focus:border-[#BA6D52] transition-colors";
const LABEL = "block text-[10px] uppercase tracking-[0.18em] text-[#757067] mb-2";

const BETRIEBSARTEN = [
  "Restaurant", "Hotel", "Sternerestaurant", "Bistro / Café", "Catering",
  "Betriebsrestaurant", "Klinik / Senioreneinrichtung", "Event-Location", "Sonstiges",
];

export default function VermittlungBetrieb() {
  const [stamm, setStamm] = useState({ bereiche: {}, positionen: {}, provision_rate_eur: 40, placement_fee_eur: 100 });
  const [form, setForm] = useState({
    placement_type: "temporary",
    betrieb: "", betriebsart: "Restaurant", ansprechpartner: "", email: "", telefon: "",
    strasse: "", plz: "", ort: "", land: "DE", ust_id: "",
    von_datum: "", bis_datum: "", einsatztage: 1, anzahl_koeche: 1,
    taeglich_von: "", taeglich_bis: "",
    eintritt_ab: "", gehalt_brutto_monat: "", unbefristet: true,
    position: "chef_de_partie", beschreibung: "", budget_stundensatz_eur: "",
    unterkunft_gestellt: false, verpflegung_gestellt: false, agb_consent: false,
    signature_name: "", signature_ort: "", signature_data_url: "",
  });
  const [bereiche, setBereiche] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [signNow, setSignNow] = useState(false);
  const [uidCheck, setUidCheck] = useState(null);
  const [uidBusy, setUidBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/vermittlung/stammdaten`).then((r) => setStamm(r.data)).catch(() => {});
  }, []);

  // Einsatztage automatisch aus dem Zeitraum berechnen
  useEffect(() => {
    if (form.placement_type !== "temporary" || !form.von_datum || !form.bis_datum) return;
    const a = new Date(form.von_datum), b = new Date(form.bis_datum);
    if (Number.isNaN(a) || Number.isNaN(b) || b < a) return;
    const days = Math.round((b - a) / 86400000) + 1;
    setForm((f) => (f.einsatztage === days ? f : { ...f, einsatztage: days }));
  }, [form.von_datum, form.bis_datum, form.placement_type]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const isTemp = form.placement_type === "temporary";
  const isAT = form.land === "AT";
  const provisionTotal = (Number(stamm.provision_rate_eur || 40) * Number(form.einsatztage || 1) * Number(form.anzahl_koeche || 1));

  const checkUid = async () => {
    const vat = (form.ust_id || "").trim();
    if (!vat) return null;
    setUidBusy(true);
    try {
      const r = await axios.post(`${API}/vermittlung/uid-pruefen`, { vat_id: vat });
      setUidCheck(r.data);
      return r.data;
    } catch {
      setUidCheck({ status: "unavailable", message: "Prüfung nicht möglich — ich prüfe die UID vor der Rechnung." });
      return null;
    } finally {
      setUidBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!bereiche.length) return toast.error("Bitte mindestens einen Bereich auswählen");
    if (!form.agb_consent) return toast.error("Bitte die Datenschutzhinweise bestätigen");
    const plzLen = isAT ? 4 : 5;
    if (!/^\d+$/.test(form.plz.trim()) || form.plz.trim().length !== plzLen)
      return toast.error(`Bitte eine ${plzLen}-stellige Postleitzahl für ${isAT ? "Österreich" : "Deutschland"} angeben`);
    if (isAT && !form.ust_id.trim())
      return toast.error("Bitte Ihre UID-Nummer angeben — sie wird für die Rechnung nach Österreich benötigt");
    if (isAT) {
      const res = uidCheck ?? (await checkUid());
      if (res?.status === "format_error") return toast.error(res.message);
      if (res?.status === "invalid" && !window.confirm(
        `${res.message}\n\nAnfrage trotzdem senden? Ich kläre die UID dann persönlich mit Ihnen.`)) return;
    }
    if (signNow && (!form.signature_data_url || !form.signature_name.trim()))
      return toast.error("Bitte Namen eintragen und im Feld unterschreiben — oder das Häkchen entfernen");
    setBusy(true);
    try {
      const payload = { ...form, bereiche };
      if (!signNow) {
        payload.signature_name = "";
        payload.signature_ort = "";
        payload.signature_data_url = "";
      }
      ["einsatztage", "anzahl_koeche"].forEach((k) => (payload[k] = Number(payload[k]) || 1));
      ["gehalt_brutto_monat", "budget_stundensatz_eur"].forEach((k) => {
        payload[k] = payload[k] === "" ? null : Number(payload[k]);
      });
      ["von_datum", "bis_datum", "eintritt_ab"].forEach((k) => {
        if (!payload[k]) payload[k] = null;
      });
      const r = await axios.post(`${API}/vermittlung/anfragen`, payload);
      setDone(r.data.request_number);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Anfrage konnte nicht gesendet werden");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 md:py-32" data-testid="betrieb-request-success">
        <Check className="w-10 h-10 text-[#BA6D52]" strokeWidth={1.4} />
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-8">Anfrage ist raus.</h1>
        <p className="text-[#575349] mt-6 leading-relaxed">
          Vielen Dank — Ihr Vorgang <b className="text-[#2C2A26]">{done}</b> liegt mir vor. Ich prüfe jetzt,
          welcher Koch aus meinem Netzwerk zu Ihnen passt, und melde mich innerhalb von 24 Stunden.
          Eine Eingangsbestätigung ist gerade unterwegs.
        </p>
        <p className="text-sm text-[#757067] mt-6 leading-relaxed">
          Bei akutem Ausfall rufen Sie mich gerne direkt an: +49 151 27282560.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="betrieb-request-page">
      <p className="text-[10px] uppercase tracking-[0.35em] text-[#BA6D52]">Für Gastronomie & Hotellerie</p>
      <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-6">Mietkoch anfordern</h1>
      <p className="text-[#757067] font-light text-lg mt-6 leading-relaxed">
        Sagen Sie mir, wann und was Sie brauchen — ich melde mich mit einem passenden Koch aus meinem
        Netzwerk. Alle Unterlagen sind vorab geprüft.
      </p>

      <form onSubmit={submit} className="mt-14 space-y-12">
        <section>
          <h2 className="text-base md:text-lg font-serif border-b border-[#E3DECE] pb-3">Was suchen Sie?</h2>
          <div className="grid sm:grid-cols-2 gap-3 mt-6">
            {[
              ["temporary", "Mietkoch auf Zeit", `${stamm.provision_rate_eur || 40} € netto pro Koch und Einsatztag`],
              ["permanent", "Festanstellung besetzen", `${stamm.placement_fee_eur || 100} € netto einmalig bei erfolgreicher Vermittlung`],
            ].map(([k, title, sub]) => (
              <button type="button" key={k} onClick={() => setForm((f) => ({ ...f, placement_type: k }))}
                data-testid={`betrieb-type-${k}`}
                className={`text-left p-5 border transition-colors ${form.placement_type === k ? "border-[#BA6D52] bg-[#FAF5F1]" : "border-[#E3DECE] bg-white hover:border-[#BA6D52]"}`}>
                <span className="block text-sm font-medium text-[#2C2A26]">{title}</span>
                <span className="block text-xs text-[#757067] mt-1 leading-relaxed">{sub}</span>
              </button>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-base md:text-lg font-serif border-b border-[#E3DECE] pb-3">Ihr Betrieb</h2>
          <div className="grid sm:grid-cols-2 gap-5 mt-6">
            <div><label className={LABEL}>Betrieb *</label><input className={FIELD} required value={form.betrieb} onChange={set("betrieb")} data-testid="betrieb-name" /></div>
            <div>
              <label className={LABEL}>Betriebsart</label>
              <select className={FIELD} value={form.betriebsart} onChange={set("betriebsart")} data-testid="betrieb-art">
                {BETRIEBSARTEN.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div><label className={LABEL}>Ansprechpartner *</label><input className={FIELD} required value={form.ansprechpartner} onChange={set("ansprechpartner")} data-testid="betrieb-ansprechpartner" /></div>
            <div><label className={LABEL}>Telefon *</label><input className={FIELD} required value={form.telefon} onChange={set("telefon")} data-testid="betrieb-telefon" /></div>
            <div className="sm:col-span-2"><label className={LABEL}>E-Mail *</label><input type="email" className={FIELD} required value={form.email} onChange={set("email")} data-testid="betrieb-email" /></div>
            <div className="sm:col-span-2"><label className={LABEL}>Straße & Hausnummer</label><input className={FIELD} value={form.strasse} onChange={set("strasse")} data-testid="betrieb-strasse" /></div>
            <div>
              <label className={LABEL}>Land *</label>
              <select className={FIELD} value={form.land} onChange={set("land")} data-testid="betrieb-land">
                <option value="DE">Deutschland</option>
                <option value="AT">Österreich</option>
              </select>
            </div>
            <div><label className={LABEL}>PLZ * ({isAT ? "4" : "5"}-stellig)</label>
              <input className={FIELD} required inputMode="numeric" maxLength={isAT ? 4 : 5}
                value={form.plz} onChange={set("plz")} data-testid="betrieb-plz" /></div>
            <div><label className={LABEL}>Ort *</label><input className={FIELD} required value={form.ort} onChange={set("ort")} data-testid="betrieb-ort" /></div>
            {isAT && (
              <div className="sm:col-span-2">
                <label className={LABEL}>UID-Nummer *</label>
                <div className="flex gap-3">
                  <input className={FIELD} value={form.ust_id}
                    onChange={(e) => { setUidCheck(null); setForm((f) => ({ ...f, ust_id: e.target.value.toUpperCase() })); }}
                    onBlur={() => checkUid()} placeholder="ATU12345678" data-testid="betrieb-uid" />
                  <button type="button" onClick={() => checkUid()} disabled={uidBusy}
                    data-testid="betrieb-uid-check"
                    className="shrink-0 border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] disabled:opacity-60 px-5 text-[11px] uppercase tracking-[0.18em] transition-colors">
                    {uidBusy ? "Prüfe …" : "Prüfen"}
                  </button>
                </div>
                {uidCheck && (
                  <p data-testid="betrieb-uid-status"
                    className={`text-[11px] mt-2 leading-relaxed ${uidCheck.status === "valid" ? "text-green-700"
                      : uidCheck.status === "unavailable" ? "text-[#757067]" : "text-[#9E5841]"}`}>
                    {uidCheck.status === "valid" ? "✓ Im EU-Register bestätigt" : "•"} {uidCheck.message}
                    {uidCheck.name ? ` (${uidCheck.name})` : ""}
                  </p>
                )}
                <p className="text-[11px] text-[#757067] mt-2 leading-relaxed">
                  Für Betriebe in Österreich stelle ich meine Provisionsrechnung ohne Umsatzsteuer aus
                  (Reverse Charge, Art. 196 MwStSystRL). Ihre UID wird direkt im EU-Register (VIES) geprüft.
                </p>
              </div>
            )}
          </div>
        </section>

        <section>
          <h2 className="text-base md:text-lg font-serif border-b border-[#E3DECE] pb-3">
            {isTemp ? "Einsatz" : "Die Stelle"}
          </h2>
          {isTemp ? (
            <>
              <div className="grid sm:grid-cols-2 gap-5 mt-6">
                <div><label className={LABEL}>Von *</label><input type="date" className={FIELD} required value={form.von_datum} onChange={set("von_datum")} data-testid="betrieb-von" /></div>
                <div><label className={LABEL}>Bis *</label><input type="date" className={FIELD} required value={form.bis_datum} onChange={set("bis_datum")} data-testid="betrieb-bis" /></div>
                <div><label className={LABEL}>Einsatztage</label><input type="number" min="1" className={FIELD} value={form.einsatztage} onChange={set("einsatztage")} data-testid="betrieb-tage" /></div>
                <div><label className={LABEL}>Anzahl Köche</label><input type="number" min="1" max="10" className={FIELD} value={form.anzahl_koeche} onChange={set("anzahl_koeche")} data-testid="betrieb-anzahl" /></div>
                <div><label className={LABEL}>Täglich von</label><input type="time" className={FIELD} value={form.taeglich_von} onChange={set("taeglich_von")} data-testid="betrieb-zeit-von" /></div>
                <div><label className={LABEL}>Täglich bis</label><input type="time" className={FIELD} value={form.taeglich_bis} onChange={set("taeglich_bis")} data-testid="betrieb-zeit-bis" /></div>
              </div>
              <div className="mt-6 bg-[#F2EFE9] p-5 text-sm text-[#575349] leading-relaxed" data-testid="betrieb-provision-preview">
                Vermittlungsprovision: <b>{stamm.provision_rate_eur || 40} €</b> netto × {form.einsatztage || 1} Einsatztage
                × {form.anzahl_koeche || 1} Koch/Köche = <b className="text-[#2C2A26]">{provisionTotal.toFixed(2)} € netto</b>
                {isAT ? " — ohne deutsche USt. (Reverse Charge)" : " zzgl. MwSt."}
                <span className="block text-xs text-[#757067] mt-2">
                  Das ist alles, was Sie an mich zahlen. Der Koch rechnet seine Leistung direkt mit Ihnen ab.
                </span>
              </div>
            </>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5 mt-6">
              <div><label className={LABEL}>Eintritt ab *</label><input type="date" className={FIELD} required value={form.eintritt_ab} onChange={set("eintritt_ab")} data-testid="betrieb-eintritt" /></div>
              <div><label className={LABEL}>Gehalt (€ brutto/Monat)</label><input type="number" step="100" className={FIELD} value={form.gehalt_brutto_monat} onChange={set("gehalt_brutto_monat")} data-testid="betrieb-gehalt" /></div>
              <div className="sm:col-span-2">
                <label className="flex items-start gap-3 text-sm text-[#575349] cursor-pointer">
                  <input type="checkbox" className="mt-1 accent-[#BA6D52]" checked={form.unbefristet} onChange={set("unbefristet")} data-testid="betrieb-unbefristet" />
                  <span>Unbefristete Stelle</span>
                </label>
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-5 mt-6">
            <div>
              <label className={LABEL}>Gesuchte Position</label>
              <select className={FIELD} value={form.position} onChange={set("position")} data-testid="betrieb-position">
                {Object.entries(stamm.positionen || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            {isTemp && (
              <div><label className={LABEL}>Ihr Budget (€ netto/Stunde)</label><input type="number" step="0.5" className={FIELD} value={form.budget_stundensatz_eur} onChange={set("budget_stundensatz_eur")} data-testid="betrieb-budget" /></div>
            )}
          </div>

          <p className={`${LABEL} mt-8`}>Welcher Bereich? *</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stamm.bereiche || {}).map(([k, label]) => (
              <button type="button" key={k}
                onClick={() => setBereiche(bereiche.includes(k) ? bereiche.filter((x) => x !== k) : [...bereiche, k])}
                data-testid={`betrieb-bereich-${k}`}
                className={`px-4 py-2 text-xs border transition-colors ${bereiche.includes(k) ? "bg-[#BA6D52] text-white border-[#BA6D52]" : "bg-white border-[#E3DECE] text-[#575349] hover:border-[#BA6D52]"}`}>
                {label}
              </button>
            ))}
          </div>

          <div className="mt-8">
            <label className={LABEL}>Aufgabe & Besonderheiten</label>
            <textarea rows={4} className={FIELD} value={form.beschreibung} onChange={set("beschreibung")} data-testid="betrieb-beschreibung"
              placeholder="Welcher Posten, wie viele Gäste, à la carte oder Bankett, Besonderheiten der Küche …" />
          </div>

          {isTemp && (
            <div className="mt-6 space-y-3">
              <label className="flex items-start gap-3 text-sm text-[#575349] cursor-pointer">
                <input type="checkbox" className="mt-1 accent-[#BA6D52]" checked={form.unterkunft_gestellt} onChange={set("unterkunft_gestellt")} data-testid="betrieb-unterkunft" />
                <span>Unterkunft wird gestellt</span>
              </label>
              <label className="flex items-start gap-3 text-sm text-[#575349] cursor-pointer">
                <input type="checkbox" className="mt-1 accent-[#BA6D52]" checked={form.verpflegung_gestellt} onChange={set("verpflegung_gestellt")} data-testid="betrieb-verpflegung" />
                <span>Verpflegung wird gestellt</span>
              </label>
            </div>
          )}
        </section>

        <div className="bg-[#F2EFE9] p-6">
          <p className="text-sm text-[#575349] leading-relaxed">
            Die Provisionsvereinbarung können Sie vorab in Ruhe lesen. Sie können sie entweder{" "}
            <b className="text-[#2C2A26]">gleich hier digital unterschreiben</b> oder später — dann
            schicke ich Ihnen einen persönlichen Link, sobald ich Ihnen einen konkreten Koch
            vorschlagen kann.
          </p>
          <a href={`${API}/vermittlung/vertrag/betrieb.pdf`} target="_blank" rel="noreferrer"
            data-testid="betrieb-vertrag-download"
            className="inline-flex items-center gap-2 mt-4 border border-[#BA6D52] text-[#BA6D52] hover:bg-white px-5 py-3 text-[11px] uppercase tracking-[0.2em] transition-colors">
            <FileDown className="w-4 h-4" /> Provisionsvereinbarung lesen
          </a>

          <label className="flex items-start gap-3 text-sm text-[#575349] cursor-pointer mt-6 pt-6 border-t border-[#E3DECE]">
            <input type="checkbox" className="mt-1 accent-[#BA6D52]" checked={signNow}
              onChange={(e) => setSignNow(e.target.checked)} data-testid="betrieb-sign-now" />
            <span>
              Ich möchte die Provisionsvereinbarung <b className="text-[#2C2A26]">jetzt direkt digital
              unterschreiben</b> — dann erhalte ich sie sofort unterschrieben als PDF per E-Mail.
            </span>
          </label>

          {signNow && (
            <div className="mt-6 space-y-4" data-testid="betrieb-sign-block">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Name der unterzeichnenden Person *</label>
                  <input className={FIELD} value={form.signature_name} onChange={set("signature_name")}
                    placeholder="Vor- und Nachname" data-testid="betrieb-sign-name" />
                </div>
                <div>
                  <label className={LABEL}>Ort</label>
                  <input className={FIELD} value={form.signature_ort} onChange={set("signature_ort")}
                    placeholder={form.ort || "Ort"} data-testid="betrieb-sign-ort" />
                </div>
              </div>
              <SignaturePad
                onChange={(v) => setForm((f) => ({ ...f, signature_data_url: v }))}
                testId="betrieb-signature"
              />
              <p className="text-xs text-[#757067] leading-relaxed">
                Mit dem Absenden erkennen Sie die Provisionsvereinbarung in der vorliegenden Fassung an.
                Abgerechnet werden ausschließlich tatsächlich geleistete Einsatztage — ohne Vermittlung
                entstehen keine Kosten.
              </p>
            </div>
          )}
        </div>

        <label className="flex items-start gap-3 text-sm text-[#575349] cursor-pointer">
          <input type="checkbox" className="mt-1 accent-[#BA6D52]" checked={form.agb_consent} onChange={set("agb_consent")} data-testid="betrieb-dsgvo" />
          <span>
            Ich habe die <a href="/datenschutz" target="_blank" rel="noreferrer" className="text-[#BA6D52] underline">Datenschutzhinweise</a> gelesen
            und bin mit der Verarbeitung meiner Angaben zum Zweck der Vermittlung einverstanden. *
          </span>
        </label>

        <button type="submit" disabled={busy} data-testid="betrieb-submit"
          className="w-full sm:w-auto bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-10 py-4 text-[11px] uppercase tracking-[0.25em] transition-colors">
          {busy ? "Wird gesendet …" : "Anfrage senden"}
        </button>
      </form>
    </div>
  );
}
